import { ArrowRight, Bell, BellRing, CalendarDays, Check, Sparkles, Wallet } from "lucide-react";
import { LazyPhoto, photoHost } from "../Cards";
import { amountWithCurrency, departureLabel, mxn } from "../../lib/reservations";
import type { SavedTrip, TripCard } from "../../types";
import { SaveTripButton } from "./SaveTripButton";

const SHADE = "linear-gradient(180deg, rgba(0,37,87,0.05) 0%, rgba(0,37,87,0.88) 100%)";

/**
 * A published trip (E3, design turno 6): image, name, duration, then on the body the highlights,
 * price basis and the action that opens it. On Guardados the same card also shows the wallet progress block, a
 * price change, the price-alert bell and a compare checkbox.
 */
export function TripProductCard({ trip, onClick, placement = "explore", saved, selectable = false, selected = false, onSelect, onToggleAlert }: {
  trip: TripCard;
  onClick(): void;
  placement?: string;
  /** Present on the Guardados page: the collection entry behind this card. */
  saved?: SavedTrip;
  selectable?: boolean;
  selected?: boolean;
  onSelect?(): void;
  onToggleAlert?(next: boolean): void;
}) {
  const change = saved?.priceChange ?? null;
  return (
    <article className={`trip-card${selected ? " selected" : ""}`}>
      <div className="trip-card-media">
        <button type="button" className="trip-card-photo photo-host" style={photoHost(trip.heroImageUrl, SHADE)} onClick={onClick} aria-label={`Abrir ${trip.name}`}>
          <LazyPhoto url={trip.heroImageUrl} sizes="(max-width: 760px) 100vw, (max-width: 1200px) 50vw, 400px" />
          <span className="hero-eyebrow">{[trip.destination?.country ?? trip.countries[0] ?? trip.region?.name ?? trip.cities[0], trip.durationDays ? `${trip.durationDays} días` : null].filter(Boolean).join(" · ")}</span>
          <strong>{trip.name}</strong>
        </button>
        <SaveTripButton trip={trip} placement={placement} />
        {change?.direction === "down" && <span className="price-drop-badge"><Sparkles size={11} aria-hidden="true" /> Bajó de precio</span>}
        {selectable && (
          <label className={`compare-check${selected ? " on" : ""}`}>
            <input type="checkbox" checked={selected} onChange={() => onSelect?.()} aria-label={`Comparar ${trip.name}`} />
            <span className="consent-box" aria-hidden="true">{selected && <Check size={13} strokeWidth={3} />}</span>
            Comparar
          </label>
        )}
      </div>
      <div className="trip-card-body">
        {saved?.apartado ? (
          <div className="card-wallet">
            <div className="card-wallet-head"><span><Wallet size={12} aria-hidden="true" /> Apartado para este viaje</span><strong translate="no">{Math.round((saved.apartado.progress ?? 0) * 100)}%</strong></div>
            <div className="card-wallet-bar"><span style={{ width: `${Math.round((saved.apartado.progress ?? 0) * 100)}%` }} /></div>
            <small translate="no">{mxn(saved.apartado.balance)} de {mxn(saved.apartado.goalAmount)} apartados</small>
          </div>
        ) : saved?.request ? (
          <div className="card-wallet muted">
            <div className="card-wallet-head"><span><Wallet size={12} aria-hidden="true" /> Solicitud en curso</span><strong translate="no">#{saved.request.folio}</strong></div>
            <small>Tu asesor revisa esta solicitud. Puedes crear el Apartado desde Mis solicitudes.</small>
          </div>
        ) : trip.highlights.length > 0 ? (
          <div className="trip-card-chips">{trip.highlights.slice(0, 3).map((item) => <span key={item} className="tag">{item}</span>)}</div>
        ) : null}
        {/* No route or summary on the card: their length varied so much that the cards looked uneven
            (23 Oct 2026). The trip page shows the route. */}
        <div className="trip-card-foot">
          <div>
            <small>Desde · por persona</small>
            <span className="price-line">
              <strong translate="no">{trip.priceFrom ? amountWithCurrency(trip.priceFrom.amount, trip.priceFrom.currency) : "Consultar"}</strong>
              {change && <s translate="no" title="Precio cuando lo guardaste">{amountWithCurrency(change.previous, change.currency)}</s>}
            </span>
            <span>{trip.nextDeparture ? <><CalendarDays size={12} aria-hidden="true" /> Próxima salida {departureLabel(trip.nextDeparture)}</> : trip.arrivalDays.join(" y ") || "Consulta fechas"}</span>
          </div>
          <div className="trip-card-actions">
            {saved && onToggleAlert && (
              <button type="button" className={`icon-button alert-bell${saved.priceAlert ? " on" : ""}`} onClick={() => onToggleAlert(!saved.priceAlert)} aria-pressed={saved.priceAlert} title={saved.priceAlert ? "Aviso de precio activo · desactivar" : "Avisarme si baja el precio"} aria-label={saved.priceAlert ? "Desactivar aviso de precio" : "Avisarme si baja el precio"}>
                {saved.priceAlert ? <BellRing size={15} aria-hidden="true" /> : <Bell size={15} aria-hidden="true" />}
              </button>
            )}
            <button type="button" className="secondary-button small" onClick={onClick}>Ver viaje <ArrowRight size={14} aria-hidden="true" /></button>
          </div>
        </div>
      </div>
    </article>
  );
}
